import * as React from 'react';
import { useStores } from '../../stores';
import { observer } from 'mobx-react-lite';
import { useTranslation } from "react-i18next";
import { ScheduleEntry } from '../../domain/ScheduleEntry';
import moment from 'moment';
import WeekSwitcher from '../../components/WeekSwitcher';
import SchedulePageTabs from './SchedulePageTabs';
import SchedulePageInfoBox from './SchedulePageInfoBox';
import ScheduleSkippedWeekTabs from './ScheduleSkippedWeekTabs';
import { isMobileOnly } from 'react-device-detect';
import { LinearProgress } from '@mui/material';
import { useConfirm } from 'material-ui-confirm';

export type ScheduleView = "default" | "fit";

interface Props {
    editable?: boolean;
    onEntryUpdate?: (entry: ScheduleEntry) => void;
    onEntryEdit?: (entry: ScheduleEntry) => void;
    onPublisherScheduleHistory?: (entry: ScheduleEntry) => void;
    onEntryAssignmentHistory?: (entry: ScheduleEntry) => void;
    onEntryDownload?: (entry: ScheduleEntry) => void;
    onEntryAutoArrange?: (entry: ScheduleEntry) => void;
}

const ScheduleContent = observer(({ editable, onEntryUpdate, onEntryEdit, onPublisherScheduleHistory, onEntryAssignmentHistory, onEntryDownload, onEntryAutoArrange }: Props) => {
    const { t } = useTranslation();
    const { serverStore, scheduleStore, localStorageStore } = useStores();
    const confirm = useConfirm();

    const handleSendNotification = React.useCallback((entry: ScheduleEntry) => {
        confirm({
            title: t('are_you_sure'),
            description: t('send_schedule_entry_notification_confirm', { title: entry.name }),
            confirmationText: t('yes'),
            cancellationText: t('no'),
        }).then(() => scheduleStore.notifyEntry(entry)).catch(() => undefined);
    }, [confirm, scheduleStore, t]);

    const handleAutoArrangeEntry = React.useCallback((entry: ScheduleEntry) => {
        confirm({
            title: t('are_you_sure'),
            description: t('this_will_rewrite_selected_schedule_entry', { title: entry.name }),
            confirmationText: t('yes'),
            cancellationText: t('no'),
        }).then(() => {
            scheduleStore.autoArrangeEntry(entry);
            onEntryAutoArrange && onEntryAutoArrange(entry);
        });
    }, [confirm, onEntryAutoArrange, scheduleStore, t]);

    const [currentDate, setCurrentDate] = React.useState<Date>(() => moment().startOf("week").toDate());

    React.useEffect(() => {
        if (!scheduleStore.weeks.length) {
            scheduleStore.getWeek(currentDate, true);
        }
    }, [scheduleStore, currentDate]);

    const handleDateUpdate = React.useCallback((date: Date) => {
        scheduleStore.getWeek(date);
        setCurrentDate(date);
    }, [scheduleStore]);

    const currentWeek = scheduleStore.currentWeek ?? null;
    const entries = React.useMemo<ScheduleEntry[]>(() => {
        if (!currentWeek) {
            return [];
        }

        return [...currentWeek.entries].sort((a, b) => a.position - b.position);
    }, [currentWeek]);
    const scheduleView = localStorageStore.getScheduleView(isMobileOnly);
    const isLoading = serverStore.serverRequestLoading;

    let weekContent: React.ReactNode;

    if (!currentWeek) {
        weekContent = (
            <>
                {isLoading && <LinearProgress />}
                <SchedulePageInfoBox text={t("no_current_week")} />
            </>
        );
    } else if (currentWeek.skip) {
        weekContent = (
            <ScheduleSkippedWeekTabs
                week={currentWeek}
                entries={entries}
                editable={editable}
                view={scheduleView}
                onEntryUpdate={onEntryUpdate}
                onEntryEdit={onEntryEdit}
                onPublisherScheduleHistory={onPublisherScheduleHistory}
                onEntryAssignmentHistory={onEntryAssignmentHistory}
                onEntrySendNotification={handleSendNotification}
                onEntryAutoArrange={handleAutoArrangeEntry}
            />
        );
    } else {
        weekContent = (
            <SchedulePageTabs
                week={currentWeek}
                entries={entries}
                editable={editable}
                view={scheduleView}
                onEntryUpdate={onEntryUpdate}
                onEntryEdit={onEntryEdit}
                onPublisherScheduleHistory={onPublisherScheduleHistory}
                onEntryAssignmentHistory={onEntryAssignmentHistory}
                onEntrySendNotification={handleSendNotification}
                onEntryDownload={onEntryDownload}
                onEntryAutoArrange={handleAutoArrangeEntry}
            />
        );
    }

    return (
        <>
            <WeekSwitcher date={currentDate} onDateUpdate={handleDateUpdate} fullWidth={isMobileOnly} />
            {weekContent}
            <br />
            <WeekSwitcher date={currentDate} onDateUpdate={handleDateUpdate} fullWidth={isMobileOnly} />
        </>
    );
});

export default ScheduleContent;